import type { Animal } from "../animals";
import { nameStep } from "../handwriting";

// A pastel chibi winged unicorn (Harder / level 10), rebuilt 1:1 from the
// user's reference image (side view, facing left, mid-walk). All coordinates
// were measured from the reference with scripts/trace-ref.mjs + ink scanlines
// — see the PR for the feature inventory and deviation ledger.
//
// Colour notes: the reference's true pastels sit closer together than the
// crayon engine's merge threshold (palette-check showed pink/peach,
// pink/lilac and peach/cream merging), so PINK is nudged more saturated and
// CREAM more yellow — same hues, wider separation; all six then stay separate
// crayon steps. The blush is exactly the mane pink so it joins the pink step.
const INK = "#1f1d26"; // bold kawaii outline
const PINK = "#f0b0c0"; // mane/tail rim bands + cheek blush
const PEACH = "#f4dac1"; // creamy middle bands (tail + fringe base)
const LILAC = "#d7c3da"; // inner swirl bands
const PERI = "#dadffd"; // the wing
const CREAM = "#f0e88e"; // the horn

// Axis-aligned ellipse as a two-arc path.
const ell = (cx: number, cy: number, rx: number, ry: number) =>
  `M ${cx - rx},${cy} a ${rx},${ry} 0 1,0 ${2 * rx},0 a ${rx},${ry} 0 1,0 ${-2 * rx},0`;

// --- head: one sweep — brow under the fringe, nose-bridge dip, muzzle, jaw --
const HEAD =
  "M 37.2,74.4 C 39.4,75.6 41.2,77.0 41.7,78.4 C 42.2,80.1 41.8,81.4 40.8,82.4 " +
  "C 38.9,84.4 36.4,87.2 35.4,90.0 C 34.9,91.6 34.9,93.4 35.6,95.2 " +
  "C 36.5,97.7 38.3,99.8 41.0,101.6 C 43.5,103.3 47.0,104.3 51.0,104.6 " +
  "C 56.5,105.0 62.0,103.7 65.5,102.0 C 66.8,101.4 68.0,100.9 68.8,100.4";

// --- ear: a leaf with its tip at the top-RIGHT — one long convex sweep on
// the left, short steep right edge; base ends tuck under the hair ----------
const EAR =
  "M 69.6,49.8 C 72.8,46.9 75.9,44.3 78.8,42.4 C 80.5,41.3 82.5,40.5 84.0,40.5 " +
  "C 84.8,41.5 85.2,43.5 85.5,46.0 C 85.8,48.0 86.4,50.0 87.2,51.4";

// --- face ------------------------------------------------------------------
const EYE_STROKE = ell(53.4, 88.5, 1.8, 4.0);
const EYE_FILL = ell(53.4, 88.5, 2.8, 5.0);
const SMILE = "M 48.9,96.2 C 49.0,99.0 48.3,101.4 45.7,101.9";
const BLUSH = ell(59.3, 91.8, 2.9, 2.5);

// --- chest + standing front leg (one line), the raised-knee bow, far leg ----
const FRONTLEG =
  "M 70.4,98.0 C 70.7,105.5 71.2,114.0 71.4,122.0 C 71.6,132.0 71.7,142.0 71.5,150.0 " +
  "C 71.4,155.0 71.7,158.6 73.2,160.7 C 74.7,162.4 77.0,162.6 79.5,162.6 " +
  "C 82.0,162.6 84.6,162.5 86.2,161.5 C 88.4,159.9 89.6,157.0 89.9,153.5 " +
  "C 90.2,148.0 90.3,141.5 90.3,134.9";
const BOW =
  "M 71.0,121.0 C 66.5,123.2 63.0,127.0 62.5,131.0 C 62.1,134.6 64.1,138.2 67.3,140.0 " +
  "C 68.7,140.7 70.2,141.0 71.6,141.1";
const FARFRONT =
  "M 100.4,136.2 C 100.1,140.5 99.4,145.0 98.4,148.2 C 97.7,150.6 96.2,151.9 94.2,152.0 " +
  "C 92.4,152.0 91.1,151.2 90.2,149.6";

// --- tummy, back + rump, hind legs -----------------------------------------
const BELLY =
  "M 90.3,135.4 C 95.0,136.6 100.0,136.8 105.0,136.3 C 110.0,135.8 115.5,134.9 120.2,134.3";
const BACK =
  "M 108.6,104.0 C 114.0,103.0 120.5,102.0 126.0,101.9 C 129.5,102.0 132.3,102.9 133.9,104.7 " +
  "C 135.9,107.0 137.1,110.5 137.6,114.0 C 138.3,120.0 138.9,127.5 139.2,135.2";
const NEARHIND =
  "M 139.2,135.2 C 139.7,141.5 139.9,148.0 139.5,152.5 C 139.2,156.5 138.0,159.9 135.8,161.7 " +
  "C 133.6,163.4 130.6,163.6 127.8,163.2 C 124.7,162.7 122.5,160.7 121.6,158.3 " +
  "C 121.2,155.9 121.1,151.5 121.1,147.0 C 121.1,142.5 120.7,138.0 120.3,134.3";
const FARHIND =
  "M 112.7,134.6 C 112.7,140.0 112.8,146.0 113.1,151.0 C 113.3,154.5 113.8,157.7 114.7,159.6 " +
  "C 115.5,161.2 116.8,161.6 118.2,160.9 C 119.7,160.1 120.7,158.8 121.2,157.2";

// --- tail: fat S-swoosh behind the rump — dome, right bulge, curl-hook,
//     bottom lobe, S-waist, comma curl back onto the rump -------------------
const TAIL_EDGE =
  "M 130.8,101.4 C 132.5,95.0 136.5,89.5 141.5,86.3 C 145.5,83.7 150.5,81.9 155.5,82.0 " +
  "C 160.5,82.1 165.7,84.6 169.8,88.6 C 173.2,91.5 175.6,96.0 176.5,102.5 " +
  "C 176.9,106.5 176.0,110.6 174.2,114.0 C 172.8,116.6 171.5,119.5 170.6,122.1 " +
  "C 170.1,122.9 170.2,123.6 170.9,124.1 C 176.5,124.3 178.6,123.0 179.3,120.2 " +
  "C 179.1,119.1 179.5,118.4 179.9,118.4 C 180.4,118.4 180.7,119.0 180.7,120.0 " +
  "C 180.9,123.0 180.6,126.1 179.7,128.7 C 178.9,131.0 177.9,132.6 176.9,133.5 " +
  "C 174.7,136.7 171.6,139.4 167.8,141.3 C 165.8,142.3 163.7,143.0 161.5,143.4 " +
  "C 156.5,143.8 151.8,142.3 149.3,139.4 " +
  "C 146.9,136.5 144.5,132.4 144.0,128.2 C 143.6,124.4 144.6,119.9 146.3,116.0 " +
  "C 147.9,112.4 149.5,108.9 150.0,106.2 C 150.4,104.6 149.7,103.2 148.4,102.2 " +
  "C 146.4,100.8 143.6,100.5 141.0,101.3 C 138.9,102.0 136.9,103.4 135.4,104.4";
const TAIL_FILL = TAIL_EDGE + " C 133.6,103.7 132.0,102.5 130.8,101.4 Z";
// pink rim: outer edge from the base over the dome to the curl-hook, inner
// boundary from the traced reference band
const TAIL_PINK =
  "M 130.8,101.4 C 132.5,95.0 136.5,89.5 141.5,86.3 C 145.5,83.7 150.5,81.9 155.5,82.0 " +
  "C 160.5,82.1 165.7,84.6 169.8,88.6 C 173.2,91.5 175.6,96.0 176.5,102.5 " +
  "C 176.9,106.5 176.0,110.6 174.2,114.0 C 172.8,116.6 171.5,119.5 170.6,122.1 " +
  "C 170.1,122.9 170.2,123.6 170.9,124.1 C 176.5,124.3 178.6,123.0 179.3,120.2 " +
  "C 179.1,119.1 179.5,118.4 179.9,118.4 C 180.4,118.4 180.7,119.0 180.7,120.0 " +
  "C 180.9,123.0 180.6,126.1 179.7,128.7 C 178.9,131.0 177.9,132.6 176.9,133.5 " +
  "C 173.8,131.9 170.3,131.6 167.0,131.2 C 164.5,131.0 162.0,129.3 160.9,127.0 " +
  "C 160.3,124.8 160.3,122.0 160.5,120.0 C 162.5,115.5 165.0,111.0 166.1,107.6 " +
  "C 167.0,104.7 167.2,101.9 166.7,100.0 C 166.0,97.4 165.3,95.4 164.4,94.0 " +
  "C 163.2,92.2 161.6,90.6 159.6,89.4 C 158.2,88.6 156.6,88.0 155.0,87.8 " +
  "C 152.8,87.5 150.4,87.9 148.2,89.0 C 145.2,90.6 142.2,92.4 139.6,94.6 " +
  "C 137.5,96.4 135.6,99.5 134.0,103.0 C 132.9,102.5 131.8,101.9 130.8,101.4 Z";
// lilac: inner S band from the comma curl through the waist, sweeping along
// the inside of the bottom lobe to the curl-hook (traced with --bands)
const TAIL_LILAC =
  "M 137.0,101.5 C 140.0,98.2 144.0,96.2 148.0,96.4 C 151.5,96.6 154.8,98.6 156.6,101.6 " +
  "C 158.0,104.0 158.2,107.0 157.4,110.0 C 156.2,114.8 153.4,119.6 152.2,124.4 " +
  "C 151.4,127.4 151.6,130.6 153.4,133.0 C 155.6,136.0 159.4,137.8 163.6,137.6 " +
  "C 168.6,137.4 173.6,134.4 177.4,128.4 C 176.6,132.2 174.4,135.8 171.0,138.4 " +
  "C 167.4,141.2 162.6,142.4 158.0,141.4 C 153.4,140.4 149.2,137.6 146.9,133.6 " +
  "C 145.4,130.8 144.8,127.4 145.4,124.2 C 146.4,118.4 149.2,112.8 150.0,106.8 " +
  "C 150.2,104.6 149.5,102.6 147.8,101.6 C 145.7,100.4 142.6,100.5 139.9,101.4 " +
  "C 137.8,101.9 136.2,103.2 134.8,104.1 C 135.4,103.2 136.2,102.3 137.0,101.5 Z";

// --- wing: swept back, hooked tip, two sharp feather notches; the left edge
//     hides behind the mane locks so only the visible edge is stroked -------
const WING_EDGE =
  "M 110.0,57.3 C 116.5,57.3 123.5,56.5 129.0,55.3 C 133.0,54.4 137.0,52.2 140.0,50.0 " +
  "C 142.5,48.1 144.6,45.9 146.3,43.6 " +
  "C 147.1,45.2 147.5,47.1 147.5,49.0 C 147.6,55.0 146.4,62.5 143.8,68.5 " +
  "C 141.9,72.6 140.2,75.0 138.0,76.7 C 135.9,78.2 133.4,79.4 130.8,80.1 " +
  "C 134.0,80.6 137.1,81.0 139.7,81.5 C 137.5,85.3 134.2,88.6 130.2,91.6 " +
  "C 127.0,93.2 122.9,93.0 119.0,92.4 C 121.0,94.2 122.8,96.4 124.2,98.8 " +
  "C 120.0,101.6 115.0,103.1 109.6,102.9";
const WING_FILL =
  WING_EDGE +
  " L 108.4,102.7 C 107.2,98.5 105.6,93.5 103.0,89.5 C 101.8,84.0 101.0,76.0 102.2,69.0 " +
  "C 103.5,64.5 106.5,60.3 110.0,57.3 Z";

// --- fringe: sharp tip left, swoop behind the horn, mound before the ear ---
const FRINGE_MAIN =
  "M 43.2,51.4 C 41.8,53.4 40.5,55.7 39.4,58.2 C 38.0,61.2 34.5,64.9 26.3,66.3 " +
  "C 29.5,68.9 33.5,71.9 37.4,74.4 C 40.9,76.1 45.0,76.7 49.0,76.7 " +
  "C 54.5,76.7 60.5,75.0 64.6,71.5 C 66.4,69.9 67.3,67.8 67.5,65.5 " +
  "C 67.8,62.2 68.0,58.5 68.4,55.9 C 68.8,53.8 69.5,51.8 70.2,50.3";
// the mound is a HIGH arc off the horn's right edge, cresting at (58.3,42.7)
const FRINGE_MOUND =
  "M 49.0,45.1 C 51.4,43.7 54.7,42.7 58.3,42.7 C 62.0,42.8 65.3,43.7 67.2,45.5 " +
  "C 68.7,47.0 69.6,48.9 69.9,50.6";
const FRINGE_FILL =
  "M 43.2,51.4 C 45.0,49.2 46.9,47.1 49.0,45.1 C 51.4,43.7 54.7,42.7 58.3,42.7 " +
  "C 62.0,42.8 65.3,43.7 67.2,45.5 C 68.7,47.0 69.6,48.9 69.9,50.6 L 70.2,50.3 " +
  "C 69.5,51.8 68.8,53.8 68.4,55.9 C 68.0,58.5 67.8,62.2 67.5,65.5 " +
  "C 67.3,67.8 66.4,69.9 64.6,71.5 " +
  "C 60.5,75.0 54.5,76.7 49.0,76.7 C 45.0,76.7 40.9,76.1 37.4,74.4 " +
  "C 33.5,71.9 29.5,68.9 26.3,66.3 C 34.5,64.9 38.0,61.2 39.4,58.2 " +
  "C 40.5,55.7 41.8,53.4 43.2,51.4 Z";
const FRINGE_PINK =
  "M 43.2,51.4 C 45.0,49.2 46.9,47.1 49.0,45.1 C 51.4,43.7 54.7,42.7 58.3,42.7 " +
  "C 62.0,42.8 65.3,43.7 67.2,45.5 C 68.7,47.0 69.6,48.9 69.9,50.6 " +
  "C 65.4,49.5 60.6,49.4 56.4,50.7 C 50.5,52.5 45.2,56.3 41.0,61.2 " +
  "C 39.3,63.2 37.9,65.3 36.9,67.4 C 33.3,67.2 29.7,66.9 26.3,66.3 " +
  "C 34.5,64.9 38.0,61.2 39.4,58.2 C 40.5,55.7 41.8,53.4 43.2,51.4 Z";
// lilac: thin band hugging the WHOLE underside, ear base to tip (--bands)
const FRINGE_LILAC =
  "M 66.8,58.6 C 66.2,61.6 65.8,64.6 65.4,67.4 C 64.4,70.6 61.4,73.0 56.8,74.2 " +
  "C 52.8,75.2 48.8,75.6 45.2,75.2 C 40.8,74.0 36.8,72.4 33.4,70.6 " +
  "C 36.6,71.6 40.0,72.4 43.4,72.5 C 48.4,72.6 53.4,71.9 57.4,70.0 " +
  "C 60.2,68.6 62.4,66.6 63.6,64.0 C 64.4,62.2 65.2,60.4 66.2,58.8 " +
  "C 66.4,58.7 66.6,58.6 66.8,58.6 Z";

// --- mane: three overlapping locks (top → bottom → the big one in front) ---
const LOCK_TOP =
  "M 87.6,50.4 C 92.5,47.2 98.5,47.3 103.2,50.0 C 106.1,51.7 108.3,54.1 109.8,57.2 " +
  "C 107.1,59.8 104.3,61.8 101.4,63.0";
const LOCKTOP_PINK =
  "M 87.6,50.4 C 89.4,49.2 91.4,48.5 93.5,48.4 C 93.5,51.7 94.0,55.1 94.7,58.4 " +
  "C 92.5,57.0 90.3,55.4 88.2,53.8 L 87.6,50.4 Z";
const LOCKTOP_PEACH =
  "M 93.0,48.3 C 96.8,47.9 100.6,48.6 103.8,50.4 C 106.5,52.0 108.5,54.3 109.8,57.2 " +
  "C 107.1,59.8 104.3,61.8 101.4,63.0 C 99.0,61.1 96.5,59.4 94.2,58.2 " +
  "C 93.5,54.9 93.0,51.6 93.0,48.3 Z";
const LOCK_MID =
  "M 88.0,54.2 C 91.5,55.8 94.9,57.7 97.4,60.0 C 99.8,62.4 101.2,66.2 101.6,70.5 " +
  "C 101.9,74.6 101.2,79.1 99.4,83.1 L 98.0,85.7 " +
  "M 88.0,54.2 C 86.5,56.8 85.1,59.4 83.7,62.0 C 82.2,64.9 80.2,64.4 78.9,65.3 " +
  "C 77.7,66.2 77.9,67.7 79.1,68.3 C 79.9,68.7 80.3,69.3 80.0,70.2 " +
  "C 79.8,70.7 79.6,71.3 79.4,71.8 " +
  "C 78.0,76.1 76.4,80.4 75.6,84.8 C 74.9,88.6 75.3,92.6 76.6,96.0 " +
  "C 77.3,97.5 78.1,98.5 79.0,99.2 " +
  "M 79.0,99.2 C 80.3,98.0 81.6,96.7 82.9,95.3";
const LOCKMID_PINK =
  "M 88.0,54.2 C 91.5,55.8 94.9,57.7 97.4,60.0 C 99.8,62.4 101.2,66.2 101.6,70.5 " +
  "C 101.9,74.6 101.2,79.1 99.4,83.1 C 98.9,84.0 98.5,84.9 98.0,85.7 " +
  "C 95.2,87.3 92.2,89.1 89.3,91.0 C 86.9,92.5 84.7,93.9 82.9,95.3 " +
  "C 81.9,97.2 80.6,98.4 79.0,99.2 C 79.6,95.7 80.2,92.3 80.9,89.0 " +
  "C 84.0,85.9 87.3,82.9 89.8,80.2 C 91.9,77.9 93.1,75.7 93.2,73.4 " +
  "C 93.3,71.1 93.2,68.8 93.2,66.4 C 91.5,63.8 89.3,61.5 86.6,59.6 " +
  "C 87.1,57.8 87.5,56.0 88.0,54.2 Z";
const LOCKMID_LILAC =
  "M 82.0,65.4 C 83.6,66.4 84.8,67.5 85.4,68.8 C 86.0,70.2 86.1,71.7 85.8,73.2 " +
  "C 83.5,79.6 80.6,85.9 76.4,92.0 C 75.9,88.8 75.7,85.6 75.8,82.4 " +
  "C 77.2,78.0 79.3,74.0 81.9,70.3 C 82.4,68.8 82.3,67.2 81.8,65.6 " +
  "C 81.9,65.5 81.9,65.5 82.0,65.4 Z";
const LOCKMID_PEACH =
  "M 86.6,59.8 C 89.0,61.9 91.1,64.1 93.0,66.4 L 93.0,73.4 " +
  "C 92.1,75.8 91.0,78.1 89.6,80.2 C 86.7,83.4 83.5,86.4 80.2,89.2 " +
  "C 79.6,92.6 79.3,95.9 79.0,99.2 C 78.3,96.6 77.8,94.0 77.4,91.4 " +
  "C 79.9,85.3 82.8,79.4 86.0,73.6 C 86.1,71.9 86.0,70.2 85.6,68.6 " +
  "C 84.7,67.4 83.5,66.3 82.2,65.4 C 83.5,63.5 85.0,61.6 86.6,59.8 Z";
const LOCK_BOT =
  "M 83.5,94.6 C 85.2,93.1 87.2,91.7 89.3,90.6 C 92.5,88.3 95.9,86.6 98.7,86.5 " +
  "C 100.1,86.5 101.1,87.2 101.8,88.4 " +
  "C 104.6,91.6 106.7,95.6 107.7,99.6 C 108.6,103.1 108.5,107.1 107.6,110.6 " +
  "C 106.9,114.3 106.1,118.0 104.9,121.2 C 101.9,121.4 98.5,120.5 95.2,118.7 " +
  "C 91.0,116.2 87.5,112.4 85.8,108.3 C 84.2,104.4 83.2,99.6 83.0,95.2";
const LOCKBOT_PINK =
  "M 84.0,94.2 C 87.2,91.6 90.8,89.4 94.7,87.6 C 95.3,87.4 95.9,87.2 96.5,87.0 " +
  "C 99.0,87.3 101.0,87.9 101.8,88.4 C 104.6,91.6 106.7,95.6 107.7,99.6 " +
  "C 108.6,103.1 108.5,107.1 107.6,110.6 C 106.7,114.1 106.1,118.0 104.9,121.2 " +
  "C 103.0,119.4 101.6,117.4 100.9,115.2 C 100.3,109.2 100.3,103.0 100.4,96.9 " +
  "C 98.5,94.3 96.3,92.0 93.8,90.2 C 90.4,91.3 87.1,92.6 84.0,94.2 Z";
const LOCKBOT_LILAC =
  "M 83.0,95.2 C 83.2,99.6 84.2,104.4 85.8,108.3 C 87.5,112.4 91.0,116.2 95.2,118.7 " +
  "C 96.9,119.6 98.7,120.3 100.4,120.6 C 97.5,116.9 94.6,113.3 92.3,109.5 " +
  "C 90.0,105.7 87.7,101.5 86.4,97.3 C 85.9,95.6 85.6,94.0 85.5,92.5 " +
  "C 84.6,93.4 83.8,94.3 83.0,95.2 Z";
const LOCKBOT_PEACH =
  "M 85.5,92.5 C 89.4,91.9 93.4,92.3 96.7,94.1 C 100.0,95.9 102.3,99.1 103.1,102.7 " +
  "C 103.9,106.5 103.4,110.8 101.9,114.7 C 101.2,116.6 100.5,118.4 100.4,120.0 " +
  "C 97.5,116.9 94.6,113.3 92.3,109.5 C 90.0,105.7 87.7,101.5 86.4,97.3 " +
  "C 85.9,95.6 85.6,94.0 85.5,92.5 Z";

// --- the white body IS a fill --------------------------------------------
// One #ffffff fill covering every enclosed white region (trunk+head+near
// legs, the far legs, the raised-leg bow, the ear). The colour engine treats
// near-white as PAPER, so the player is unchanged (paper renders as the
// canvas) — but the home-card/sticker thumbnails render authored fills
// literally, and without this the body showed the dark card straight
// through in dark mode. Closure seams run under ink lines or under the
// hair/lock fills painted later.
const BODY_PAPER =
  "M 37.2,74.4 C 39.4,75.6 41.2,77.0 41.7,78.4 C 42.2,80.1 41.8,81.4 40.8,82.4 " +
  "C 38.9,84.4 36.4,87.2 35.4,90.0 C 34.9,91.6 34.9,93.4 35.6,95.2 " +
  "C 36.5,97.7 38.3,99.8 41.0,101.6 C 43.5,103.3 47.0,104.3 51.0,104.6 " +
  "C 56.5,105.0 62.0,103.7 65.5,102.0 C 66.8,101.4 68.0,100.9 68.8,100.4 " +
  "L 70.4,98.0 C 70.7,105.5 71.2,114.0 71.4,122.0 C 71.6,132.0 71.7,142.0 71.5,150.0 " +
  "C 71.4,155.0 71.7,158.6 73.2,160.7 C 74.7,162.4 77.0,162.6 79.5,162.6 " +
  "C 82.0,162.6 84.6,162.5 86.2,161.5 C 88.4,159.9 89.6,157.0 89.9,153.5 " +
  "C 90.2,148.0 90.3,141.5 90.3,134.9 C 95.0,136.4 100.0,136.7 105.0,136.3 " +
  "C 110.0,135.8 115.5,134.9 120.3,134.3 C 120.7,138.0 121.1,142.5 121.1,147.0 " +
  "C 121.1,151.5 121.6,156.2 122.6,159.0 C 123.5,161.4 125.4,162.8 127.8,163.2 " +
  "C 130.6,163.6 133.6,163.4 135.8,161.7 C 138.0,159.9 139.2,156.5 139.5,152.5 " +
  "C 139.9,148.0 139.7,141.5 139.2,135.2 C 138.9,127.5 138.3,120.0 137.6,114.0 " +
  "C 137.1,110.5 135.9,107.0 133.9,104.7 C 132.3,102.9 129.5,102.0 126.0,101.9 " +
  "C 120.5,102.0 114.0,103.0 108.6,104.0 " +
  "C 107.5,98.0 106.5,93.0 104.6,89.0 C 103.4,84.0 102.7,76.0 103.9,69.0 " +
  "C 105.2,64.0 107.5,60.0 110.5,57.5 C 104.5,52.6 98.0,50.4 92.0,50.2 L 87.2,51.4 " +
  "C 86.4,50.0 85.8,48.0 85.5,46.0 C 85.2,43.5 84.8,41.5 84.0,40.5 " +
  "C 82.5,40.5 80.5,41.3 78.8,42.4 C 75.9,44.3 72.8,46.9 69.6,49.8 " +
  "L 70.0,50.6 C 62.0,52.0 52.0,56.0 44.0,62.0 C 40.5,66.0 38.5,70.0 37.2,74.4 Z " +
  "M 100.4,136.2 C 100.1,140.5 99.4,145.0 98.4,148.2 C 97.7,150.6 96.2,151.9 94.2,152.0 " +
  "C 92.4,152.0 91.1,151.2 90.2,149.6 L 90.3,138.0 C 93.0,137.2 96.7,136.7 100.4,136.2 Z " +
  "M 112.7,134.6 C 112.7,140.0 112.8,146.0 113.1,151.0 C 113.3,154.5 113.8,157.7 114.7,159.6 " +
  "C 115.5,161.2 116.8,161.6 118.2,160.9 C 119.7,160.1 120.7,158.8 121.2,157.2 " +
  "L 121.1,147.0 C 121.1,142.5 120.7,138.0 120.3,134.3 C 117.8,134.5 115.2,134.6 112.7,134.6 Z " +
  "M 71.0,121.0 C 66.5,123.2 63.0,127.0 62.5,131.0 C 62.1,134.6 64.1,138.2 67.3,140.0 " +
  "C 68.7,140.7 70.2,141.0 71.6,141.1 L 71.5,121.0 Z";

// --- horn: leaning stacked cone in front of the fringe, 4 sagging rings ----
// the right edge flows smoothly from tip to base like the left one — the
// reference's stacked-roll step at the nub read as a bump (user preference)
const HORN_OUTLINE =
  "M 38.7,37.4 C 40.3,36.5 42.1,37.1 43.1,38.7 C 43.8,39.8 45.8,42.2 48.3,44.4 " +
  "C 50.8,46.6 54.5,49.7 58.3,52.2 " +
  "C 60.5,53.6 62.2,55.4 63.6,57.6 C 63.0,60.8 60.9,63.6 57.8,65.3 " +
  "C 54.8,66.8 51.4,66.9 48.6,66.1 C 47.7,64.7 47.0,63.2 46.4,61.6 " +
  "C 44.6,56.5 42.8,51.4 41.2,46.2 C 40.3,43.3 39.4,40.3 38.7,37.4 Z";
// four rings at even fifths along the horn, both ends ON the edges, each
// bowing gently toward the base (user preference: even spacing beats the
// reference's freehand ring placement and its fading bottom ring)
const HORN_RINGS =
  "M 41.7,47.4 Q 47.1,48.2 49.4,44.8 M 42.9,51.7 Q 49.4,52.0 52.8,48.1 " +
  "M 44.4,55.9 Q 52.1,55.6 56.7,51.1 M 45.9,60.2 Q 55.4,59.9 61.9,55.3";
const HORN_FILL = HORN_OUTLINE;

export const unicorn10: Animal = {
  id: "unicorn-10",
  name: "Unicorn",
  emoji: "🦄",
  viewBox: "0 0 200 200",
  color: INK,
  level: 10,
  steps: [
    { hint: "Draw the head with a soft snout 🐴", strokes: [HEAD], strokeWidth: 3 },
    { hint: "Add a pointy ear 👂", strokes: [EAR], strokeWidth: 3 },
    {
      hint: "Add the front legs — one prancing! 🦵",
      strokes: [FRONTLEG, BOW, FARFRONT],
      strokeWidth: 3,
    },
    {
      hint: "Draw the back and the round tummy 🐴",
      strokes: [BELLY, BACK],
      strokeWidth: 3,
    },
    { hint: "Add the back legs 🦵", strokes: [NEARHIND, FARHIND], strokeWidth: 3 },
    { hint: "Swish a big wavy tail 🌊", strokes: [TAIL_EDGE], strokeWidth: 3 },
    {
      // the horn comes BEFORE the hair so the fringe strokes have edges to
      // close onto — hair-first left its top hanging open for three steps
      hint: "Draw the magic horn 🌟",
      strokes: [HORN_OUTLINE, HORN_RINGS],
      strokeWidth: 2.2,
    },
    {
      hint: "Sweep the fringe around the horn 💇",
      strokes: [FRINGE_MAIN, FRINGE_MOUND],
      strokeWidth: 3,
    },
    {
      hint: "Flow the wavy mane down the neck 🌊",
      strokes: [LOCK_TOP, LOCK_BOT, LOCK_MID],
      strokeWidth: 3,
    },
    { hint: "Spread a magical wing ✨", strokes: [WING_EDGE], strokeWidth: 3 },
    {
      hint: "Add an eye and a happy smile 😊",
      strokes: [EYE_STROKE, SMILE],
      strokeWidth: 2.4,
    },
    {
      hint: "Now colour your unicorn in! 🖍️",
      strokes: [],
      fills: [
        // ONE crayon step per colour: the lock pinks are RIM regions that
        // tile edge-to-edge with their bands (no full base underneath), so
        // no different-colour fill overlaps a same-colour group and the
        // splitter never has to re-split a crayon. Order: wing first (it
        // sits behind everything it touches), then all peach, all pink,
        // all lilac, then the horn over the fringe and the eye.
        { d: WING_FILL, color: PERI },
        { d: TAIL_FILL, color: PEACH, hint: "Now creamy peach swirls! 🍑" },
        // white body over the tail's base, under the hair/face fills — the
        // engine shows it as paper; the thumbnails show it white
        { d: BODY_PAPER, color: "#ffffff" },
        { d: FRINGE_FILL, color: PEACH },
        { d: LOCKTOP_PEACH, color: PEACH },
        { d: LOCKBOT_PEACH, color: PEACH },
        { d: LOCKMID_PEACH, color: PEACH },
        { d: TAIL_PINK, color: PINK, hint: "Pink for the mane! 🩷" },
        { d: FRINGE_PINK, color: PINK },
        { d: LOCKTOP_PINK, color: PINK },
        { d: LOCKBOT_PINK, color: PINK },
        { d: LOCKMID_PINK, color: PINK },
        { d: BLUSH, color: PINK },
        { d: TAIL_LILAC, color: LILAC, hint: "Lilac swirls next! 💜" },
        { d: FRINGE_LILAC, color: LILAC },
        { d: LOCKBOT_LILAC, color: LILAC },
        { d: LOCKMID_LILAC, color: LILAC },
        // the horn paints over the fringe
        { d: HORN_FILL, color: CREAM, hint: "Buttery yellow for the horn! 🌟" },
        // the eye
        { d: EYE_FILL, color: INK },
      ],
    },
    // UNICORN is all box-edge letters (U N C O R N verticals/arcs touch
    // their advance boxes), so the default 0.18 gap leaves ~0.4 units of
    // ink-to-ink air after stroke width — a picket fence. 0.32 reads right.
    nameStep("UNICORN", { baseline: 195, height: 19, gap: 0.32 }),
  ],
};
